import { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

import api from "../../services/api";
import Modal from "../../components/ui/Modal";
import { notify } from "../../components/ui/Toast";
import { readApiError } from "../../utils/apiError";
import { relativeTime, exactTime } from "../../utils/time";
import { Confirm, FormError, ModalFoot, PanelBody } from "../../components/ui/panelKit";

const LABELS = { label: "Nomi" };

/**
 * The API secrets this client system calls the core API with.
 *
 * Two things about this panel are not cosmetic:
 *
 * 1. The secret exists in readable form for exactly one response. Only its
 *    SHA-256 is stored, so there is no endpoint that could hand it back and no
 *    "show again" to offer. It is displayed in place of the form, with its own
 *    warning, and the modal cannot be reopened on it.
 * 2. Issuing does not revoke anything. Rotation is deliberately two steps --
 *    issue, deploy, then revoke -- because revoking first breaks the
 *    customer's integration the moment they ask for a new key.
 */
const CredentialsPanel = ({ clientId, onCount }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);

  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/clients/${clientId}/credentials/`);
      const list = res.data || [];
      setRows(list);
      // A revoked key is history, not capability: the stat counts live ones.
      onCount?.(list.filter((c) => c.is_active).length);
    } catch (err) {
      setError(readApiError(err, LABELS, "Kalitlar roʻyxatini yuklab boʻlmadi."));
    } finally {
      setLoading(false);
    }
  }, [clientId, onCount]);

  useEffect(() => {
    load();
  }, [load]);

  const issue = async () => {
    setSaving(true);
    setFormError("");
    try {
      const res = await api.post(`/clients/${clientId}/credentials/`, {
        label: label.trim(),
      });
      // Keep the modal open: closing it here would throw the secret away, and
      // nothing can produce it again.
      setIssued(res.data);
      setCopied(false);
      await load();
    } catch (err) {
      setFormError(readApiError(err, LABELS, "Kalit yaratib boʻlmadi."));
    } finally {
      setSaving(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(issued.secret);
      setCopied(true);
    } catch {
      // Clipboard access is refused outside a secure context. The value is on
      // screen and selectable, so say that rather than failing silently.
      notify.info("Nusxa olinmadi — kalitni belgilab, qoʻlda koʻchiring.");
    }
  };

  const closeIssued = () => {
    setOpen(false);
    setIssued(null);
    setLabel("");
  };

  const revoke = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await api.delete(`/clients/${clientId}/credentials/${confirm.id}/`);
      notify.success("Kalit bekor qilindi");
      setConfirm(null);
      await load();
    } catch (err) {
      notify.error(readApiError(err, LABELS, "Kalitni bekor qilib boʻlmadi."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ms-panel">
      <div className="ms-panel-head">
        <h2>API kalitlari</h2>
        <div className="ms-toolbar-spacer" />
        {!loading && !rows.some((c) => c.is_active) && (
          <span className="ms-badge ms-badge--progress">faol kalit yoʻq</span>
        )}
        <button
          type="button"
          className="ms-btn ms-btn--primary ms-btn--sm"
          onClick={() => {
            setLabel("");
            setFormError("");
            setIssued(null);
            setOpen(true);
          }}
        >
          <Icon icon="tabler:key-filled" />
          Kalit
        </button>
      </div>

      <PanelBody
        loading={loading}
        error={error}
        empty={!rows.length}
        emptyText="Kalit yoʻq — mijoz tizimi API ga murojaat qila olmaydi"
      >
        <div className="ms-table-wrap">
          <table className="ms-table ms-table--compact">
            <thead>
              <tr>
                <th>Nomi</th>
                <th style={{ width: 110 }}>Holati</th>
                <th style={{ width: 150 }}>Oxirgi ishlatilgan</th>
                <th className="ms-td-actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td className="ms-td-strong">{c.label || "—"}</td>
                  <td>
                    <span className={`ms-badge ms-badge--${c.is_active ? "success" : "danger"}`}>
                      {c.is_active ? "faol" : "bekor qilingan"}
                    </span>
                  </td>
                  <td
                    className="ms-td-muted"
                    title={c.last_used_at ? exactTime(c.last_used_at) : exactTime(c.created_at)}
                  >
                    {c.last_used_at ? relativeTime(c.last_used_at) : "hech qachon"}
                  </td>
                  <td className="ms-td-actions">
                    {c.is_active && (
                      <div className="ms-rowacts">
                        <button
                          type="button"
                          className="ms-rowact is-danger"
                          title="Bekor qilish"
                          aria-label={`${c.label || "kalit"}ni bekor qilish`}
                          onClick={() => setConfirm(c)}
                        >
                          <Icon icon="tabler:ban" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PanelBody>

      <Modal
        open={open}
        title={issued ? "Kalit yaratildi" : "Yangi API kaliti"}
        size="sm"
        onClose={closeIssued}
        footer={
          issued ? (
            <button type="button" className="ms-btn ms-btn--primary" onClick={closeIssued}>
              Yozib oldim, yopish
            </button>
          ) : (
            <ModalFoot
              onCancel={closeIssued}
              onSave={issue}
              saving={saving}
              saveLabel="Yaratish"
              savingLabel="Yaratilmoqda..."
            />
          )
        }
      >
        {issued ? (
          <>
            <div className="ms-secret">
              <span>Maxfiy kalit</span>
              <code className="ms-mono">{issued.secret}</code>
              <button
                type="button"
                className="ms-btn ms-btn--ghost ms-btn--sm"
                onClick={copySecret}
              >
                <Icon icon={copied ? "tabler:check" : "tabler:copy"} />
                {copied ? "Nusxa olindi" : "Nusxa olish"}
              </button>
            </div>
            <div className="ms-secret-warn">
              <Icon icon="tabler:alert-triangle" />
              <p>
                Bu kalit <b>faqat shu yerda, faqat bir marta</b> koʻrsatiladi.
                Serverda uning faqat xeshi saqlanadi, shuning uchun uni keyin
                hech qanday yoʻl bilan qayta koʻrish mumkin emas. Hozir
                koʻchirib, mijoz tizimiga bering. Yoʻqolsa — yangi kalit
                yaratib, buni bekor qilish kerak.
              </p>
            </div>
          </>
        ) : (
          <div className="ms-form-grid ms-form-grid--stack">
            <div className="ms-field ms-field--full">
              <label htmlFor="ms-cred-label">Nomi</label>
              <input
                id="ms-cred-label"
                type="text"
                maxLength={100}
                placeholder="Masalan: ishlab chiqarish"
                value={label}
                autoFocus
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && issue()}
              />
              <span className="ms-field-hint">
                Nom — bu ikki kalitni ajratish uchun, maxfiy emas. Yangi kalit
                yaratilishi eskilarini bekor qilmaydi: avval yangisini joylang,
                keyin eskisini bekor qiling.
              </span>
            </div>
            <FormError>{formError}</FormError>
          </div>
        )}
      </Modal>

      <Confirm
        open={!!confirm}
        busy={busy}
        name={confirm?.label || "nomsiz kalit"}
        title="Kalitni bekor qilish"
        message="Bu kalit bilan qilingan keyingi har bir API soʻrovi darhol rad etiladi. Kalitni qaytarib boʻlmaydi — mijoz tizimi ishlashda davom etishi uchun unga yangi kalit berish kerak."
        confirmLabel="Kalitni bekor qilish"
        cancelLabel="Yoʻq, qolsin"
        busyLabel="Bekor qilinmoqda..."
        onConfirm={revoke}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default CredentialsPanel;
